import { create } from "zustand";
import { api } from "../services/api";
import { normalizarMapa, paraGuardar } from "../domain/mapa/mapa";
import { sincronizarPartes } from "../domain/mapa/partes";
import { comoTarifa } from "../domain/mapa/tarifasNuevas";
import { useMemo } from "react";
import { historialNuevo, registrar, deshacer, rehacer } from "../domain/mapa/historial";

export const GUARDADO = {
  AL_DIA: "al_dia",
  PENDIENTE: "pendiente",
  GUARDANDO: "guardando",
  ERROR: "error",
  CONFLICTO: "conflicto",
};

// Respaldo en el navegador de lo que todavía no llegó al servidor. Lleva la
// versión sobre la que se hicieron los cambios: si al volver el servidor sigue
// en esa versión, esos cambios no se guardaron y se ofrecen para recuperar.
const claveRespaldo = (eventoId) => `mapa-borrador-${eventoId}`;

const guardarRespaldo = (eventoId, version, mapa) => {
  try {
    localStorage.setItem(claveRespaldo(eventoId), JSON.stringify({ version, mapa, fecha: Date.now() }));
  } catch {
    // sin almacenamiento local el editor sigue funcionando, sólo sin respaldo
  }
};

const leerRespaldo = (eventoId) => {
  try {
    return JSON.parse(localStorage.getItem(claveRespaldo(eventoId)));
  } catch {
    return null;
  }
};

const borrarRespaldo = (eventoId) => {
  try {
    localStorage.removeItem(claveRespaldo(eventoId));
  } catch {
    // nada que borrar
  }
};

const AGRUPAR_MS = 1500;

const useEditorMapa = create((set, get) => ({
  eventoId: null,
  cargando: true,
  errorCarga: null,
  historial: historialNuevo(null),
  version: 0,
  esBorrador: false,
  publicado: null,
  tarifas: [],
  fechas: [],
  guardado: GUARDADO.AL_DIA,
  ultimoGuardado: null,
  mensajeGuardado: null,
  publicando: false,
  seleccion: null,
  // "diseno" o "tarifas": en la de tarifas el plano sólo sirve para elegir sectores
  pestana: "diseno",
  respaldo: null,
  ultimoAgrupado: null,

  cargar: async (eventoId) => {
    set({ eventoId, cargando: true, errorCarga: null, seleccion: null, respaldo: null });
    let respuesta;
    try {
      respuesta = await api.mapa.get(eventoId);
    } catch {
      set({ cargando: false, errorCarga: "No se pudo conectar con el servidor." });
      return;
    }
    if (respuesta.status !== "success") {
      set({ cargando: false, errorCarga: respuesta.message || "No se pudo cargar el mapa." });
      return;
    }

    const datos = respuesta.data;
    const respaldo = leerRespaldo(eventoId);
    const respaldoVigente = respaldo && respaldo.version === datos.version ? respaldo : null;
    if (respaldo && !respaldoVigente) borrarRespaldo(eventoId);

    set({
      cargando: false,
      historial: historialNuevo(normalizarMapa(datos.mapa, { esViejo: !datos.publicado && !datos.es_borrador })),
      version: datos.version,
      esBorrador: datos.es_borrador,
      publicado: datos.publicado ? new Date(datos.publicado) : null,
      tarifas: datos.tarifas || [],
      fechas: datos.fechas || [],
      guardado: GUARDADO.AL_DIA,
      ultimoGuardado: datos.borrador_actualizado ? new Date(datos.borrador_actualizado) : null,
      mensajeGuardado: null,
      respaldo: respaldoVigente,
    });
  },

  recuperarRespaldo: () => {
    const { respaldo, historial } = get();
    if (!respaldo) return;
    set({
      historial: registrar(historial, normalizarMapa(respaldo.mapa)),
      respaldo: null,
      guardado: GUARDADO.PENDIENTE,
    });
  },

  descartarRespaldo: () => {
    borrarRespaldo(get().eventoId);
    set({ respaldo: null });
  },

  // cambiar aplica fn al mapa y deja el paso en el historial. Con agrupar
  // (una clave), los cambios seguidos sobre lo mismo se deshacen juntos.
  cambiar: (fn, { agrupar } = {}) => {
    const { historial, ultimoAgrupado, eventoId, version } = get();
    const nuevo = sincronizarPartes(fn(historial.presente));
    if (nuevo === historial.presente) return;
    const ahora = Date.now();
    const sumar =
      Boolean(agrupar) && ultimoAgrupado?.clave === agrupar && ahora - ultimoAgrupado.momento < AGRUPAR_MS;
    set({
      historial: registrar(historial, nuevo, { agrupar: sumar }),
      guardado: GUARDADO.PENDIENTE,
      ultimoAgrupado: agrupar ? { clave: agrupar, momento: ahora } : null,
    });
    guardarRespaldo(eventoId, version, nuevo);
  },

  deshacer: () => {
    const { historial, eventoId, version } = get();
    if (historial.pasado.length === 0) return;
    const nuevo = deshacer(historial);
    set({ historial: nuevo, guardado: GUARDADO.PENDIENTE, ultimoAgrupado: null });
    guardarRespaldo(eventoId, version, nuevo.presente);
  },

  rehacer: () => {
    const { historial, eventoId, version } = get();
    if (historial.futuro.length === 0) return;
    const nuevo = rehacer(historial);
    set({ historial: nuevo, guardado: GUARDADO.PENDIENTE, ultimoAgrupado: null });
    guardarRespaldo(eventoId, version, nuevo.presente);
  },

  seleccionar: (seleccion) => set({ seleccion }),

  cambiarPestana: (pestana) => {
    const { seleccion } = get();
    // en la de tarifas sólo se eligen sectores
    // en la de tarifas se eligen ubicaciones, filas y mesas (no objetos)
    const sigue = ["sector", "fila", "mesa", "lugares"].includes(seleccion?.tipo) || pestana === "diseno" ? seleccion : null;
    // filas y mesas mezcladas sólo se eligen para asignar tarifas
    const sigueEnDiseno = pestana === "diseno" && seleccion?.tipo === "lugares" ? null : sigue;
    set({ pestana, seleccion: sigueEnDiseno });
  },

  guardarBorrador: async () => {
    const { eventoId, version, historial, guardado } = get();
    if (guardado !== GUARDADO.PENDIENTE && guardado !== GUARDADO.ERROR) return;
    const mapa = historial.presente;
    set({ guardado: GUARDADO.GUARDANDO });

    let respuesta;
    try {
      respuesta = await api.mapa.guardarBorrador(eventoId, version, paraGuardar(mapa));
    } catch {
      set({ guardado: GUARDADO.ERROR, mensajeGuardado: "Sin conexión: se vuelve a intentar solo." });
      return;
    }
    if (respuesta.httpStatus === 409) {
      set({ guardado: GUARDADO.CONFLICTO, mensajeGuardado: respuesta.message });
      return;
    }
    if (respuesta.status !== "success") {
      set({ guardado: GUARDADO.ERROR, mensajeGuardado: respuesta.message || "No se pudo guardar." });
      return;
    }

    const actual = get().historial.presente;
    const quedaronCambios = actual !== mapa;
    set({
      version: respuesta.data.version,
      esBorrador: true,
      ultimoGuardado: new Date(respuesta.data.borrador_actualizado),
      guardado: quedaronCambios ? GUARDADO.PENDIENTE : GUARDADO.AL_DIA,
      mensajeGuardado: null,
    });
    if (quedaronCambios) {
      guardarRespaldo(eventoId, respuesta.data.version, actual);
    } else {
      borrarRespaldo(eventoId);
    }
  },

  // después de cambiar una tarifa (por ejemplo la venta por mesa) sin tocar el plano
  recargarTarifas: async () => {
    const { eventoId } = get();
    try {
      const respuesta = await api.mapa.get(eventoId);
      if (respuesta.status === "success") set({ tarifas: respuesta.data.tarifas || [], fechas: respuesta.data.fechas || [] });
    } catch {
      // queda la lista anterior; se actualiza al volver a abrir el editor
    }
  },

  publicar: async () => {
    const { eventoId, version, historial } = get();
    set({ publicando: true });
    let respuesta;
    try {
      respuesta = await api.mapa.publicar(eventoId, version, paraGuardar(historial.presente));
    } catch {
      set({ publicando: false });
      return { ok: false, mensaje: "No se pudo conectar con el servidor." };
    }
    if (respuesta.httpStatus === 409) {
      set({ publicando: false, guardado: GUARDADO.CONFLICTO, mensajeGuardado: respuesta.message });
      return { ok: false, mensaje: respuesta.message };
    }
    if (respuesta.status !== "success") {
      set({ publicando: false });
      return { ok: false, mensaje: respuesta.message || "No se pudo publicar el mapa." };
    }

    // lo publicado vuelve con ids nuevos: el historial anterior ya no sirve
    borrarRespaldo(eventoId);
    set({
      publicando: false,
      historial: historialNuevo(normalizarMapa(respuesta.data.mapa)),
      version: respuesta.data.version,
      esBorrador: false,
      publicado: respuesta.data.publicado ? new Date(respuesta.data.publicado) : new Date(),
      guardado: GUARDADO.AL_DIA,
      ultimoGuardado: null,
      seleccion: null,
    });
    get().recargarTarifas();
    return { ok: true };
  },

  descartarBorrador: async () => {
    const { eventoId, version, cargar } = get();
    const respuesta = await api.mapa.descartarBorrador(eventoId, version);
    if (respuesta.status !== "success") {
      return { ok: false, mensaje: respuesta.message || "No se pudo descartar el borrador." };
    }
    borrarRespaldo(eventoId);
    await cargar(eventoId);
    return { ok: true };
  },
}));

export const useMapaActual = () => useEditorMapa((s) => s.historial.presente);

// useTarifas: las tarifas del evento y las creadas en el mapa (sin publicar)
export const useTarifas = () => {
  const tarifas = useEditorMapa((s) => s.tarifas);
  const nuevas = useEditorMapa((s) => s.historial.presente?.tarifas_nuevas);
  return useMemo(() => [...tarifas, ...(nuevas || []).map(comoTarifa)], [tarifas, nuevas]);
};

export default useEditorMapa;
